<template>
  <div class="tree-item-crud">
    <div class="tree-item-content">
      <!-- Expand/Collapse Toggle -->
      <span 
        v-if="item.children && item.children.length" 
        class="toggle"
        @click.stop="toggleExpand"
      >
        <b-icon :icon="isExpanded ? 'minus' : 'plus'" size="is-small"></b-icon>
      </span>
      <span v-else class="toggle-spacer"></span>
      
      <!-- Category Name or Edit Input -->
      <div class="tree-item-name-section">
        <input
          v-if="isEditing"
          ref="editInput"
          v-model="editName"
          type="text"
          class="tree-edit-input"
          @keyup.enter="saveEdit"
          @keyup.esc="cancelEdit"
          @click.stop
        />
        <span v-else class="tree-item-name">{{ item.name }}</span>
      </div>

      <!-- Action Buttons -->
      <div class="tree-item-actions">
        <template v-if="!isEditing">
          <b-tooltip label="ערוך" type="is-dark" :append-to-body="true" position="is-top">
            <b-button 
              size="is-small" 
              icon-left="pencil" 
              @click.stop="startEdit"
              type="is-info"
            />
          </b-tooltip>
          
          <b-tooltip label="הוסף קטגוריה בת" type="is-dark" :append-to-body="true" position="is-top">
            <b-button 
              size="is-small" 
              icon-left="plus" 
              @click.stop="startAddChild"
              type="is-success"
            />
          </b-tooltip>
          
          <b-tooltip label="העבר קטגוריה" type="is-dark" :append-to-body="true" position="is-top">
            <b-button 
              size="is-small" 
              icon-left="arrow-left-right" 
              @click.stop="moveCategory"
              type="is-warning"
            />
          </b-tooltip>
          
          <b-tooltip :label="deleteTooltip" type="is-dark" :append-to-body="true" position="is-top">
            <b-button 
              size="is-small" 
              icon-left="delete" 
              @click.stop="deleteCategory"
              type="is-danger"
            />
          </b-tooltip>
        </template>
        <template v-else>
          <b-tooltip label="שמור" type="is-dark" :append-to-body="true" position="is-top">
            <b-button 
              size="is-small" 
              icon-left="check" 
              @click.stop="saveEdit"
              type="is-success"
            />
          </b-tooltip>
          
          <b-tooltip label="ביטול" type="is-dark" :append-to-body="true" position="is-top">
            <b-button 
              size="is-small" 
              icon-left="close" 
              @click.stop="cancelEdit"
              type="is-danger"
            />
          </b-tooltip>
        </template>
      </div>
    </div>
    
    <!-- Add Child Input -->
    <div v-if="isAddingChild" class="tree-add-child">
      <input
        ref="addChildInput"
        v-model="newChildName"
        type="text"
        class="tree-add-input"
        placeholder="שם קטגוריה חדשה..."
        @keyup.enter="saveChild"
        @keyup.esc="cancelAddChild"
        @click.stop
      />
      <div class="tree-add-actions">
        <b-button 
          size="is-small" 
          icon-left="check" 
          @click.stop="saveChild"
          type="is-success"
        >
          שמור
        </b-button>
        <b-button 
          size="is-small" 
          icon-left="close" 
          @click.stop="cancelAddChild"
          type="is-danger"
        >
          ביטול
        </b-button>
      </div>
    </div>

    <!-- Recursive Children -->
    <div v-if="isExpanded && item.children && item.children.length" class="tree-children">
      <TreeItemCRUD
        v-for="child in item.children"
        :key="child.id"
        :item="child"
        @edit="$emit('edit', $event)"
        @add-child="$emit('add-child', $event)"
        @move="$emit('move', $event)"
        @delete="$emit('delete', $event)"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'TreeItemCRUD',
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isExpanded: false,
      isEditing: false,
      isAddingChild: false,
      editName: '',
      newChildName: ''
    };
  },
  computed: {
    deleteTooltip() {
      const childCount = this.countAllDescendants(this.item);
      if (childCount > 0) {
        return `מחק קטגוריה (כולל ${childCount} קטגוריות בנות)`;
      }
      return 'מחק קטגוריה';
    }
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },
    startEdit() {
      this.isEditing = true;
      this.editName = this.item.name;
      this.$nextTick(() => {
        if (this.$refs.editInput) {
          this.$refs.editInput.focus();
          this.$refs.editInput.select();
        }
      });
    },
    saveEdit() {
      if (!this.editName.trim()) {
        this.$buefy.toast.open({
          message: 'שם הקטגוריה לא יכול להיות רק',
          type: 'is-danger',
          duration: 3000
        });
        return;
      }
      
      if (this.editName.trim() === this.item.name) {
        this.cancelEdit();
        return;
      }

      this.$emit('edit', {
        id: this.item.id,
        name: this.editName.trim(),
        parentId: this.item.parentId
      });
      this.isEditing = false;
    },
    cancelEdit() {
      this.isEditing = false;
      this.editName = '';
    },
    startAddChild() {
      this.isAddingChild = true;
      this.isExpanded = true;
      this.$nextTick(() => {
        if (this.$refs.addChildInput) {
          this.$refs.addChildInput.focus();
        }
      });
    },
    saveChild() {
      if (!this.newChildName.trim()) {
        this.$buefy.toast.open({
          message: 'שם הקטגוריה לא יכול להיות רק',
          type: 'is-danger',
          duration: 3000
        });
        return;
      }

      this.$emit('add-child', {
        parentId: this.item.id,
        name: this.newChildName.trim()
      });
      this.isAddingChild = false;
      this.newChildName = '';
    },
    cancelAddChild() {
      this.isAddingChild = false;
      this.newChildName = '';
    },
    moveCategory() {
      this.$emit('move', {
        id: this.item.id,
        name: this.item.name,
        currentParentId: this.item.parentId,
        item: this.item
      });
    },
    deleteCategory() {
      this.$emit('delete', {
        id: this.item.id,
        name: this.item.name,
        childCount: this.countAllDescendants(this.item)
      });
    },
    countAllDescendants(category) {
      if (!category.children || !category.children.length) {
        return 0;
      }
      let count = category.children.length;
      category.children.forEach(child => {
        count += this.countAllDescendants(child);
      });
      return count;
    }
  }
};
</script>

<style scoped lang="scss">
.tree-item-crud {
  user-select: none;
}

.tree-item-content {
  display: flex;
  align-items: center;
  padding: 8px 5px;
  gap: 10px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #fff;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f8f9fa;
  }
}

.toggle {
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;
  flex-shrink: 0;

  &:hover {
    background-color: #e9ecef;
  }
}

.toggle-spacer {
  width: 24px;
  display: inline-block;
  flex-shrink: 0;
}

.tree-item-name-section {
  flex: 1;
  min-width: 0;
}

.tree-item-name {
  font-weight: 500;
  color: #2c3e50;
  word-break: break-word;
}

.tree-edit-input {
  width: 100%;
  padding: 6px 10px;
  border: 2px solid #3273dc;
  border-radius: 4px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #1e5a9e;
  }
}

.tree-item-actions {
  display: flex;
  gap: 5px;
  flex-shrink: 0;
}

.tree-add-child {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px 10px 50px;
  background-color: #f0f8ff;
  border-bottom: 1px solid #d0e8ff;
}

.tree-add-input {
  flex: 1;
  padding: 8px 12px;
  border: 2px solid #28a745;
  border-radius: 4px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #1e7e34;
  }
}

.tree-add-actions {
  display: flex;
  gap: 5px;
}

.tree-children {
  padding-right: 30px;
  background-color: #fafafa;
}
</style>

<style lang="scss">
// Global styles for tooltips to fix Hebrew text RTL
::v-deep .tooltip {
  z-index: 9999 !important;
  
  .tooltip-content {
    white-space: nowrap;
    direction: rtl;
    min-width: max-content;
    padding: 0.5rem 0.75rem;
    z-index: 9999 !important;
  }
}
</style>
